import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Globe, ImageIcon, InboxIcon, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import { listPagesPaged } from "@/api/pages";
import { listDomainsPaged } from "@/api/domains";
import {
    listAdvertisers,
    assignBannerAdvertiser,
    assignDomainAdvertiser,
    assignDraftAdvertiser,
    assignPageAdvertiser,
    type AdvertiserDto,
} from "@/api/advertisers";
import { api } from "@/lib/api";
import type { DraftDto } from "@/types/constructor";

type EntityKind = "page" | "domain" | "draft" | "banner";

interface UnassignedRow {
    kind: EntityKind;
    id: number;
    name: string;
    updatedAt: string;
    extra?: string | null;
    href?: string;
}

const KIND_LABEL: Record<EntityKind, string> = {
    page: "Page",
    domain: "Domain",
    draft: "Draft",
    banner: "Banner",
};

const KIND_ICON: Record<EntityKind, React.ReactNode> = {
    page: <FileText className="h-3.5 w-3.5" />,
    domain: <Globe className="h-3.5 w-3.5" />,
    draft: <Sparkles className="h-3.5 w-3.5" />,
    banner: <ImageIcon className="h-3.5 w-3.5" />,
};

const UNASSIGNED_FILTER = -1; // matches backend convention: advertiserId=-1 → IS NULL

export function UnassignedPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const [kindFilter, setKindFilter] = React.useState<EntityKind | "all">("all");
    const [selected, setSelected] = React.useState<Set<string>>(new Set());

    // /admin/unassigned isn't a real endpoint yet — we query each entity list
    // with advertiserId=-1 (the backend convention for IS NULL) and merge the
    // results client-side. Each query is independent so a single 500 only
    // hides that entity type.
    const queries = useQueries({
        queries: [
            {
                queryKey: ["unassigned", "pages"],
                queryFn: () => listPagesPaged({ size: 200, sort: "updatedAt,desc", advertiserId: UNASSIGNED_FILTER }),
            },
            {
                queryKey: ["unassigned", "domains"],
                queryFn: () => listDomainsPaged({ size: 200, sort: "updatedAt,desc", advertiserId: UNASSIGNED_FILTER }),
            },
            {
                queryKey: ["unassigned", "drafts"],
                queryFn: async () => {
                    const { data } = await api.get<{ content: DraftDto[] }>("/admin/constructor/drafts", {
                        params: { size: 200, advertiserId: UNASSIGNED_FILTER },
                    });
                    return data.content ?? [];
                },
            },
        ],
    });
    const pagesQuery = queries[0];
    const domainsQuery = queries[1];
    const draftsQuery = queries[2];

    // Banners need pageId to list — no cross-page banners endpoint exists yet,
    // so we omit them from the unassigned merge view. The "Banner" kind is left
    // in the filter dropdown so the empty state explains the limitation.
    const advertisersQuery = useQuery({
        queryKey: ["advertisers", "list", "active"],
        queryFn: () => listAdvertisers(false),
    });

    const rows: UnassignedRow[] = React.useMemo(() => {
        const out: UnassignedRow[] = [];
        if (pagesQuery.data) {
            for (const p of pagesQuery.data.content) {
                out.push({
                    kind: "page",
                    id: p.id,
                    name: p.name,
                    updatedAt: p.updatedAt,
                    extra: p.description,
                    href: `/pages/${p.id}`,
                });
            }
        }
        if (domainsQuery.data) {
            for (const d of domainsQuery.data.content) {
                out.push({
                    kind: "domain",
                    id: d.id,
                    name: d.hostname,
                    updatedAt: d.updatedAt,
                    extra: d.activePageName ?? d.status,
                    href: `/domains/${d.id}`,
                });
            }
        }
        if (draftsQuery.data) {
            for (const d of draftsQuery.data) {
                out.push({
                    kind: "draft",
                    id: d.id,
                    name: d.name,
                    updatedAt: d.updatedAt,
                    extra: d.ownerEmail,
                    href: `/constructor/draft/${d.id}`,
                });
            }
        }
        out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        return out;
    }, [pagesQuery.data, domainsQuery.data, draftsQuery.data]);

    const filteredRows = React.useMemo(() => {
        if (kindFilter === "all") return rows;
        return rows.filter((r) => r.kind === kindFilter);
    }, [rows, kindFilter]);

    const assignMut = useMutation({
        mutationFn: async ({ row, advertiserId }: { row: UnassignedRow; advertiserId: number | null }) => {
            const body = { advertiserId, reason: null };
            switch (row.kind) {
                case "page": await assignPageAdvertiser(row.id, body); break;
                case "domain": await assignDomainAdvertiser(row.id, body); break;
                case "draft": await assignDraftAdvertiser(row.id, body); break;
                case "banner": await assignBannerAdvertiser(row.id, body); break;
            }
        },
        onSuccess: () => {
            toast.success("Assigned");
            queryClient.invalidateQueries({ queryKey: ["unassigned"] });
            queryClient.invalidateQueries({ queryKey: ["advertisers"] });
            queryClient.invalidateQueries({ queryKey: ["advertiser"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't assign")),
    });

    const bulkAssignMut = useMutation({
        mutationFn: async ({ targets, advertiserId }: { targets: UnassignedRow[]; advertiserId: number | null }) => {
            const body = { advertiserId, reason: "bulk assign from unassigned" };
            const results = await Promise.allSettled(targets.map((row) => {
                switch (row.kind) {
                    case "page": return assignPageAdvertiser(row.id, body);
                    case "domain": return assignDomainAdvertiser(row.id, body);
                    case "draft": return assignDraftAdvertiser(row.id, body);
                    case "banner": return assignBannerAdvertiser(row.id, body);
                }
            }));
            const failed = results.filter((r) => r.status === "rejected").length;
            return { ok: results.length - failed, failed };
        },
        onSuccess: ({ ok, failed }) => {
            queryClient.invalidateQueries({ queryKey: ["unassigned"] });
            queryClient.invalidateQueries({ queryKey: ["advertisers"] });
            queryClient.invalidateQueries({ queryKey: ["advertiser"] });
            setSelected(new Set());
            if (failed === 0) toast.success(`Assigned ${ok} item${ok === 1 ? "" : "s"}`);
            else toast.error(`Assigned ${ok}; ${failed} failed`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Bulk assign failed")),
    });

    const allLoading = pagesQuery.isLoading || domainsQuery.isLoading || draftsQuery.isLoading;
    const rowKey = (r: UnassignedRow) => `${r.kind}:${r.id}`;
    const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(rowKey(r)));
    const toggleAll = () => {
        const next = new Set<string>();
        if (!allSelected) for (const r of filteredRows) next.add(rowKey(r));
        setSelected(next);
    };
    const toggle = (r: UnassignedRow) => {
        const next = new Set(selected);
        const k = rowKey(r);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        setSelected(next);
    };
    const selectedRows = React.useMemo(
        () => filteredRows.filter((r) => selected.has(rowKey(r))),
        [filteredRows, selected],
    );

    const [bulkPickerValue, setBulkPickerValue] = React.useState("");

    return (
        <>
            <PageHeader
                title="Unassigned"
                description="Pages, domains and drafts not yet attributed to an advertiser. Assign in place or in bulk."
                actions={
                    <div className="flex items-center gap-2">
                        {selectedRows.length > 0 && (
                            <>
                                <Select
                                    value={bulkPickerValue}
                                    onValueChange={(v) => {
                                        setBulkPickerValue(v);
                                        if (v === "") return;
                                        const advertiserId = v === "__null__" ? null : Number(v);
                                        bulkAssignMut.mutate({ targets: selectedRows, advertiserId });
                                        setBulkPickerValue("");
                                    }}
                                >
                                    <SelectTrigger className="h-9 w-[220px]" data-testid="bulk-assign-picker">
                                        <SelectValue placeholder={`Bulk assign ${selectedRows.length}…`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__null__">(Clear advertiser)</SelectItem>
                                        {(advertisersQuery.data ?? []).map((a) => (
                                            <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
                            </>
                        )}
                    </div>
                }
            />
            <div className="space-y-3 px-6 py-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as EntityKind | "all")}>
                        <SelectTrigger className="h-9 w-[180px]" data-testid="unassigned-kind-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All entity types</SelectItem>
                            <SelectItem value="page">Pages only</SelectItem>
                            <SelectItem value="domain">Domains only</SelectItem>
                            <SelectItem value="draft">Drafts only</SelectItem>
                            <SelectItem value="banner">Banners only</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-xs text-muted-foreground">
                        {filteredRows.length} unassigned{kindFilter !== "all" ? ` ${kindFilter}s` : " items"}
                    </div>
                </div>

                {allLoading ? (
                    <div className="flex justify-center py-16"><Spinner size="lg" /></div>
                ) : kindFilter === "banner" ? (
                    <EmptyState
                        icon={<ImageIcon className="h-8 w-8" />}
                        title="Banners need a page context"
                        description="Banners hang off their parent page; open the page detail to assign the banner's owner."
                    />
                ) : filteredRows.length === 0 ? (
                    <EmptyState
                        icon={<InboxIcon className="h-8 w-8" />}
                        title="Nothing unassigned"
                        description="Every page, domain and draft has an advertiser. Nice."
                    />
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="unassigned-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="w-10 px-3 py-2 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            aria-label="Select all unassigned rows"
                                            data-testid="unassigned-select-all"
                                        />
                                    </th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Last updated</th>
                                    <th className="px-3 py-2 text-left">Assign advertiser</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredRows.map((r) => (
                                    <UnassignedRow
                                        key={rowKey(r)}
                                        row={r}
                                        selected={selected.has(rowKey(r))}
                                        onToggle={() => toggle(r)}
                                        advertisers={advertisersQuery.data ?? []}
                                        onAssign={(advertiserId) => assignMut.mutate({ row: r, advertiserId })}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

function UnassignedRow({
    row,
    selected,
    onToggle,
    advertisers,
    onAssign,
}: {
    row: UnassignedRow;
    selected: boolean;
    onToggle: () => void;
    advertisers: AdvertiserDto[];
    onAssign: (advertiserId: number | null) => void;
}): React.ReactNode {
    const [value, setValue] = React.useState("");
    return (
        <tr
            className={cn("hover:bg-muted/30", selected && "bg-primary/5")}
            data-testid={`unassigned-row-${row.kind}-${row.id}`}
        >
            <td className="px-3 py-2">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={onToggle}
                    aria-label={`Select ${row.kind} ${row.name}`}
                    data-testid={`unassigned-select-${row.kind}-${row.id}`}
                />
            </td>
            <td className="px-3 py-2">
                <Badge variant="outline" className="gap-1">
                    {KIND_ICON[row.kind]}
                    {KIND_LABEL[row.kind]}
                </Badge>
            </td>
            <td className="px-3 py-2">
                {row.href ? (
                    <Link to={row.href} className="font-medium hover:underline">{row.name}</Link>
                ) : (
                    <span className="font-medium">{row.name}</span>
                )}
                {row.extra && (
                    <div className="text-xs text-muted-foreground truncate max-w-md">{row.extra}</div>
                )}
            </td>
            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(row.updatedAt)}</td>
            <td className="px-3 py-2">
                <Select
                    value={value}
                    onValueChange={(v) => {
                        setValue(v);
                        if (v === "") return;
                        const advertiserId = v === "__null__" ? null : Number(v);
                        onAssign(advertiserId);
                        // Reset to placeholder so the dropdown re-shows "Pick…" after the
                        // mutation invalidates and removes this row.
                        setValue("");
                    }}
                >
                    <SelectTrigger className="h-8 w-[200px]" data-testid={`unassigned-assign-${row.kind}-${row.id}`}>
                        <SelectValue placeholder="Pick advertiser…" />
                    </SelectTrigger>
                    <SelectContent>
                        {advertisers.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
        </tr>
    );
}
